import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSignal } from '@preact/signals-react';
import { toast } from 'react-toastify';
import { FaEdit, FaTrash, FaLock, FaFilter, FaFileDownload, FaRegistered, FaFileExcel } from 'react-icons/fa';
import PageHeader from '../../molecules/PageHeader';
import ATable, { ATableHeader } from '../../organisms/ATable';
import Dialog from '../../organisms/Dialog';
import SelectFilter from '../../organisms/SelectFilter';

// TODO: Replace mock with API call when backend is ready
import issuancesMock from '../../../data/issuances-mock.json';

interface Issuance {
    id: string;
    broker: string;
    issue_date: string;
    product: string;
    due_date: string;
    index: string;
    tax: string;
    quantity: number;
    pu_degassed: number;
    registered_b3: boolean;
    registered_dimensa: boolean;
}

const BROKER_OPTIONS = [
    { value: 'NU', label: 'NU' },
    { value: 'XP', label: 'XP' },
    { value: 'BTG', label: 'BTG' },
    { value: 'ITAU', label: 'ITAU' },
    { value: 'TORO', label: 'TORO' },
    { value: 'DAYCOVAL', label: 'DAYCOVAL' },
    { value: 'GENIAL', label: 'GENIAL' },
    { value: 'NIKOS', label: 'NIKOS' },
    { value: 'INTER', label: 'INTER' },
    { value: 'ÁGORA', label: 'ÁGORA' },
    { value: 'MIRAE', label: 'MIRAE' },
];

const PRODUCT_OPTIONS = [
    { value: 'LCA', label: 'LCA' },
    { value: 'CDB', label: 'CDB' },
    { value: 'LCI', label: 'LCI' },
    { value: 'CRI', label: 'CRI' },
    { value: 'CRA', label: 'CRA' },
];

const INDEX_OPTIONS = [
    { value: 'PRE', label: 'PRE' },
    { value: 'POS', label: 'POS' },
    { value: 'CDI', label: 'CDI' },
    { value: 'IPCA', label: 'IPCA' },
];

const TABLE_HEADERS: ATableHeader[] = [
    { id: 'col-broker', title: 'Corretora', field: 'broker', canSort: true, width: '100px' },
    { id: 'col-issue-date', title: 'Emissão', field: 'issue_date', canSort: true, width: '110px' },
    { id: 'col-product', title: 'Título', field: 'product', canSort: true, width: '80px' },
    { id: 'col-due-date', title: 'Vencimento', field: 'due_date', canSort: true, width: '110px' },
    { id: 'col-index', title: 'Índice', field: 'index', canSort: true, width: '80px' },
    { id: 'col-tax', title: 'Taxa', field: 'tax', canSort: true, width: '100px' },
    { id: 'col-quantity', title: 'Qtde', field: 'quantity', canSort: true, width: '80px' },
    { id: 'col-pu', title: 'PU Desagiado', field: 'pu_degassed', canSort: true, width: '130px' },
    { id: 'col-b3', title: 'Reg. B3', field: 'registered_b3', canSort: false, width: '90px' },
    { id: 'col-dimensa', title: 'Reg. Dimensa', field: 'registered_dimensa', canSort: false, width: '110px' },
    { id: 'col-actions', title: 'Ações', field: '', canSort: false, width: '100px' },
];

const formatCurrency = (value: number) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);

const IssuanceListPage: React.FC = () => {
    const navigate = useNavigate();

    // TODO: Replace with useFetch when API is available
    const [issuances, setIssuances] = useState<Issuance[]>(issuancesMock as Issuance[]);

    const [filterBroker, setFilterBroker] = useState('');
    const [filterProduct, setFilterProduct] = useState('');
    const [filterIndex, setFilterIndex] = useState('');
    const [filterDateFrom, setFilterDateFrom] = useState('');
    const [filterDateTo, setFilterDateTo] = useState('');

    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    const [fieldSorted, setFieldSorted] = useState('issue_date');
    const [orderSorted, setOrderSorted] = useState<'ASC' | 'DESC'>('DESC');

    const showEditModal = useSignal(false);
    const editingIssuance = useSignal<Issuance | null>(null);
    const isSending = useSignal(false);

    const filteredIssuances = useMemo(() => {
        return issuances.filter((row) => {
            if (filterBroker && row.broker !== filterBroker) return false;
            if (filterProduct && row.product !== filterProduct) return false;
            if (filterIndex && row.index !== filterIndex) return false;
            return true;
        });
    }, [issuances, filterBroker, filterProduct, filterIndex]);

    const sortedIssuances = useMemo(() => {
        const sorted = [...filteredIssuances];
        sorted.sort((a, b) => {
            const aVal = String((a as unknown as Record<string, unknown>)[fieldSorted] ?? '');
            const bVal = String((b as unknown as Record<string, unknown>)[fieldSorted] ?? '');
            if (aVal === bVal) return 0;
            const cmp = aVal < bVal ? -1 : 1;
            return orderSorted === 'ASC' ? cmp : -cmp;
        });
        return sorted;
    }, [filteredIssuances, fieldSorted, orderSorted]);

    const clearFilters = () => {
        setFilterBroker('');
        setFilterProduct('');
        setFilterIndex('');
        setFilterDateFrom('');
        setFilterDateTo('');
    };

    const toggleSelect = (id: string) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const toggleSelectAll = (checked: boolean) => {
        if (checked) {
            setSelectedIds(new Set(sortedIssuances.map((r) => r.id)));
        } else {
            setSelectedIds(new Set());
        }
    };

    const handleSort = (field: string, order: 'ASC' | 'DESC') => {
        setFieldSorted(field);
        setOrderSorted(order);
    };

    const openEdit = (issuance: Issuance) => {
        editingIssuance.value = { ...issuance };
        showEditModal.value = true;
    };

    const handleDelete = (id: string) => {
        if (!window.confirm('Deseja realmente excluir esta emissão?')) return;
        // TODO: Call API DELETE endpoint
        setIssuances((prev) => prev.filter((r) => r.id !== id));
        setSelectedIds((prev) => {
            const next = new Set(prev);
            next.delete(id);
            return next;
        });
        toast.info('Emissão excluída.');
    };

    const handleSaveEdit = () => {
        if (!editingIssuance.value) return;
        isSending.value = true;
        // TODO: Call API PUT endpoint
        const updated = editingIssuance.value;
        setIssuances((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
        showEditModal.value = false;
        editingIssuance.value = null;
        isSending.value = false;
        toast.success('Emissão atualizada.');
    };

    const updateEditField = (field: keyof Issuance, value: string | number | boolean) => {
        if (!editingIssuance.value) return;
        editingIssuance.value = { ...editingIssuance.value, [field]: value };
    };

    // Bulk actions — TODO: replace with real API calls
    const handleBulkAction = (action: string) => {
        if (selectedIds.size === 0) return;
        const count = selectedIds.size;
        switch (action) {
            case 'generate_b3_file':
                toast.info(`Gerando arquivo B3 para ${count} emissão(ões)...`);
                break;
            case 'register_b3':
                toast.info(`Registrando ${count} emissão(ões) na B3...`);
                setIssuances((prev) =>
                    prev.map((r) => (selectedIds.has(r.id) ? { ...r, registered_b3: true } : r))
                );
                break;
            case 'register_dimensa':
                toast.info(`Registrando ${count} emissão(ões) na Dimensa...`);
                setIssuances((prev) =>
                    prev.map((r) => (selectedIds.has(r.id) ? { ...r, registered_dimensa: true } : r))
                );
                break;
            case 'generate_xls':
                toast.info(`Gerando XLS controle para ${count} emissão(ões)...`);
                break;
        }
    };

    const isRegistered = (r: Issuance) => r.registered_b3 || r.registered_dimensa;

    const renderRegistrationBadge = (registered: boolean) => (
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${registered
            ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'
            : 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300'
            }`}>
            {registered ? 'Sim' : 'Não'}
            {registered && <FaLock className="w-3 h-3" />}
        </span>
    );

    const renderRows = () =>
        sortedIssuances.map((row, idx) => (
            <tr
                key={row.id}
                className={`${idx % 2 === 0 ? 'bg-slate-50 dark:bg-slate-800' : 'dark:bg-slate-900'} hover:bg-slate-200 dark:hover:bg-slate-700 border-b border-gray-200 dark:border-gray-700`}
            >
                <td className="py-2 px-2 text-center">
                    <input
                        type="checkbox"
                        checked={selectedIds.has(row.id)}
                        onChange={() => toggleSelect(row.id)}
                        className="w-4 h-4 rounded border-gray-300 dark:border-gray-600"
                    />
                </td>
                <td className="py-2 px-2 text-sm">{row.broker}</td>
                <td className="py-2 px-2 text-sm">{row.issue_date}</td>
                <td className="py-2 px-2 text-sm font-semibold">{row.product}</td>
                <td className="py-2 px-2 text-sm">{row.due_date}</td>
                <td className="py-2 px-2 text-sm">{row.index}</td>
                <td className="py-2 px-2 text-sm">{row.tax}</td>
                <td className="py-2 px-2 text-sm text-right">{row.quantity}</td>
                <td className="py-2 px-2 text-sm text-right">{formatCurrency(row.pu_degassed)}</td>
                <td className="py-2 px-2 text-center">{renderRegistrationBadge(row.registered_b3)}</td>
                <td className="py-2 px-2 text-center">{renderRegistrationBadge(row.registered_dimensa)}</td>
                <td className="py-2 px-2 text-center">
                    {isRegistered(row) ? (
                        <FaLock className="inline text-gray-400 dark:text-gray-500" title="Emissão registrada" />
                    ) : (
                        <div className="flex items-center justify-center gap-2">
                            <button onClick={() => openEdit(row)} className="text-gray-500 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400" title="Editar">
                                <FaEdit />
                            </button>
                            <button onClick={() => handleDelete(row.id)} className="text-gray-500 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400" title="Excluir">
                                <FaTrash />
                            </button>
                        </div>
                    )}
                </td>
            </tr>
        ));

    const editModalBody = editingIssuance.value ? (
        <div className="p-6 space-y-4">
            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Corretora</label>
                    <select
                        value={editingIssuance.value.broker}
                        onChange={(e) => updateEditField('broker', e.target.value)}
                        className="w-full p-2 pr-8 border border-gray-300 rounded bg-white dark:bg-slate-700 dark:border-slate-600 dark:text-white text-sm appearance-none bg-no-repeat bg-[length:16px_16px] bg-[position:right_8px_center] bg-[url('data:image/svg+xml;charset=UTF-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20viewBox%3D%220%200%2020%2020%22%20fill%3D%22%236b7280%22%3E%3Cpath%20fill-rule%3D%22evenodd%22%20d%3D%22M5.23%207.21a.75.75%200%20011.06.02L10%2011.168l3.71-3.938a.75.75%200%20111.08%201.04l-4.25%204.5a.75.75%200%2001-1.08%200l-4.25-4.5a.75.75%200%2001.02-1.06z%22%20clip-rule%3D%22evenodd%22%2F%3E%3C%2Fsvg%3E')]"
                    >
                        {BROKER_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Título</label>
                    <select
                        value={editingIssuance.value.product}
                        onChange={(e) => updateEditField('product', e.target.value)}
                        className="w-full p-2 pr-8 border border-gray-300 rounded bg-white dark:bg-slate-700 dark:border-slate-600 dark:text-white text-sm appearance-none bg-no-repeat bg-[length:16px_16px] bg-[position:right_8px_center] bg-[url('data:image/svg+xml;charset=UTF-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20viewBox%3D%220%200%2020%2020%22%20fill%3D%22%236b7280%22%3E%3Cpath%20fill-rule%3D%22evenodd%22%20d%3D%22M5.23%207.21a.75.75%200%20011.06.02L10%2011.168l3.71-3.938a.75.75%200%20111.08%201.04l-4.25%204.5a.75.75%200%2001-1.08%200l-4.25-4.5a.75.75%200%2001.02-1.06z%22%20clip-rule%3D%22evenodd%22%2F%3E%3C%2Fsvg%3E')]"
                    >
                        {PRODUCT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Data Emissão</label>
                    <input
                        type="text"
                        value={editingIssuance.value.issue_date}
                        onChange={(e) => updateEditField('issue_date', e.target.value)}
                        className="w-full p-2 border border-gray-300 rounded dark:bg-slate-700 dark:border-slate-600 dark:text-white text-sm"
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Data Vencimento</label>
                    <input
                        type="text"
                        value={editingIssuance.value.due_date}
                        onChange={(e) => updateEditField('due_date', e.target.value)}
                        className="w-full p-2 border border-gray-300 rounded dark:bg-slate-700 dark:border-slate-600 dark:text-white text-sm"
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Índice</label>
                    <select
                        value={editingIssuance.value.index}
                        onChange={(e) => updateEditField('index', e.target.value)}
                        className="w-full p-2 pr-8 border border-gray-300 rounded bg-white dark:bg-slate-700 dark:border-slate-600 dark:text-white text-sm appearance-none bg-no-repeat bg-[length:16px_16px] bg-[position:right_8px_center] bg-[url('data:image/svg+xml;charset=UTF-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20viewBox%3D%220%200%2020%2020%22%20fill%3D%22%236b7280%22%3E%3Cpath%20fill-rule%3D%22evenodd%22%20d%3D%22M5.23%207.21a.75.75%200%20011.06.02L10%2011.168l3.71-3.938a.75.75%200%20111.08%201.04l-4.25%204.5a.75.75%200%2001-1.08%200l-4.25-4.5a.75.75%200%2001.02-1.06z%22%20clip-rule%3D%22evenodd%22%2F%3E%3C%2Fsvg%3E')]"
                    >
                        {INDEX_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Taxa (%)</label>
                    <input
                        type="text"
                        value={editingIssuance.value.tax.replace('%', '')}
                        onChange={(e) => updateEditField('tax', e.target.value + '%')}
                        className="w-full p-2 border border-gray-300 rounded dark:bg-slate-700 dark:border-slate-600 dark:text-white text-sm"
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Quantidade</label>
                    <input
                        type="number"
                        value={editingIssuance.value.quantity}
                        onChange={(e) => updateEditField('quantity', Number(e.target.value))}
                        className="w-full p-2 border border-gray-300 rounded dark:bg-slate-700 dark:border-slate-600 dark:text-white text-sm"
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">PU Desagiado (R$)</label>
                    <input
                        type="number"
                        value={editingIssuance.value.pu_degassed}
                        onChange={(e) => updateEditField('pu_degassed', Number(e.target.value))}
                        className="w-full p-2 border border-gray-300 rounded dark:bg-slate-700 dark:border-slate-600 dark:text-white text-sm"
                    />
                </div>
            </div>
            <div className="flex items-center gap-6 pt-2">
                <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                    <input
                        type="checkbox"
                        checked={editingIssuance.value.registered_b3}
                        onChange={(e) => updateEditField('registered_b3', e.target.checked)}
                        disabled={editingIssuance.value.registered_b3}
                        className="w-4 h-4 rounded border-gray-300 dark:border-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                    />
                    Registrado B3
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                    <input
                        type="checkbox"
                        checked={editingIssuance.value.registered_dimensa}
                        onChange={(e) => updateEditField('registered_dimensa', e.target.checked)}
                        disabled={editingIssuance.value.registered_dimensa}
                        className="w-4 h-4 rounded border-gray-300 dark:border-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                    />
                    Registrado Dimensa
                </label>
            </div>
        </div>
    ) : null;

    const editModalFooter = (
        <div className="flex gap-2">
            <button
                onClick={() => { showEditModal.value = false; editingIssuance.value = null; }}
                className="px-4 py-2 text-sm border border-gray-300 rounded hover:bg-gray-100 dark:border-slate-600 dark:text-white dark:hover:bg-slate-700"
            >
                Cancelar
            </button>
            <button
                onClick={handleSaveEdit}
                disabled={isSending.value}
                className="px-4 py-2 text-sm bg-teal-600 text-white rounded hover:bg-teal-700 disabled:opacity-50 dark:bg-teal-700 dark:hover:bg-teal-800"
            >
                Salvar Alterações
            </button>
        </div>
    );

    return (
        <div className="px-4 bg-white dark:bg-slate-800 text-gray-900 dark:text-gray-100">
            <div className="grid grid-cols-1">
                <PageHeader
                    title="Consultar Emissões"
                    apiVersion=""
                    breadcrumb={[
                        { label: 'Tesouraria', url: '/funding/issuance/list' },
                        { label: 'Consultar Emissões', url: '#' },
                    ]}
                >
                    <div className="pt-4 flex justify-end">
                        <button
                            onClick={() => navigate('/funding/issuance/form')}
                            className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-700 text-sm dark:bg-blue-600 dark:hover:bg-blue-700"
                        >
                            Nova Operação
                        </button>
                    </div>
                </PageHeader>

                {/* Filters */}
                <div className="flex flex-wrap items-end gap-3 py-3 px-1 border-b border-gray-200 dark:border-gray-700">
                    <SelectFilter
                        id="filter-broker"
                        label="Corretora"
                        value={filterBroker}
                        onChange={setFilterBroker}
                        options={BROKER_OPTIONS}
                        defaultVoidOption
                        defaultLabel="Todas..."
                        className="w-40"
                    />
                    <SelectFilter
                        id="filter-product"
                        label="Título"
                        value={filterProduct}
                        onChange={setFilterProduct}
                        options={PRODUCT_OPTIONS}
                        defaultVoidOption
                        defaultLabel="Todos..."
                        className="w-32"
                    />
                    <SelectFilter
                        id="filter-index"
                        label="Índice"
                        value={filterIndex}
                        onChange={setFilterIndex}
                        options={INDEX_OPTIONS}
                        defaultVoidOption
                        defaultLabel="Todos..."
                        className="w-32"
                    />
                    <div className="flex items-end gap-1">
                        <div className="relative">
                            <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Período de Emissão</label>
                            <input
                                type="date"
                                value={filterDateFrom}
                                onChange={(e) => setFilterDateFrom(e.target.value)}
                                className="py-2 px-2 text-sm border border-gray-200 bg-white dark:bg-slate-800 dark:border-slate-600 dark:text-white"
                            />
                        </div>
                        <span className="text-gray-400 pb-2">-</span>
                        <input
                            type="date"
                            value={filterDateTo}
                            onChange={(e) => setFilterDateTo(e.target.value)}
                            className="py-2 px-2 text-sm border border-gray-200 bg-white dark:bg-slate-800 dark:border-slate-600 dark:text-white"
                        />
                    </div>
                    <button
                        onClick={clearFilters}
                        className="flex items-center gap-1 px-3 py-2 text-sm text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
                    >
                        <FaFilter className="w-3 h-3" /> Limpar Filtros
                    </button>
                </div>

                {/* Bulk actions bar */}
                {selectedIds.size > 0 && (
                    <div className="flex items-center gap-3 py-2 px-1 bg-blue-50 dark:bg-slate-700 border-b border-blue-200 dark:border-slate-600">
                        <span className="text-sm text-blue-700 dark:text-blue-300 font-medium">
                            {selectedIds.size} registro(s) selecionado(s)
                        </span>
                        <div className="flex gap-2 ml-auto">
                            <button
                                onClick={() => handleBulkAction('generate_b3_file')}
                                className="flex items-center gap-1 px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-100 dark:border-slate-500 dark:text-white dark:hover:bg-slate-600"
                            >
                                <FaFileDownload /> Gerar Arquivo B3
                            </button>
                            <button
                                onClick={() => handleBulkAction('register_b3')}
                                className="flex items-center gap-1 px-3 py-1.5 text-sm bg-teal-600 text-white rounded hover:bg-teal-700 dark:bg-teal-700 dark:hover:bg-teal-800"
                            >
                                <FaRegistered /> Registrar B3
                            </button>
                            <button
                                onClick={() => handleBulkAction('register_dimensa')}
                                className="flex items-center gap-1 px-3 py-1.5 text-sm bg-teal-600 text-white rounded hover:bg-teal-700 dark:bg-teal-700 dark:hover:bg-teal-800"
                            >
                                <FaRegistered /> Registrar Dimensa
                            </button>
                            <button
                                onClick={() => handleBulkAction('generate_xls')}
                                className="flex items-center gap-1 px-3 py-1.5 text-sm bg-green-600 text-white rounded hover:bg-green-700 dark:bg-green-700 dark:hover:bg-green-800"
                            >
                                <FaFileExcel /> Gerar XLS Controle
                            </button>
                        </div>
                    </div>
                )}

                {/* Table */}
                <ATable
                    headers={TABLE_HEADERS}
                    fieldSorted={fieldSorted}
                    orderSorted={orderSorted}
                    onSort={handleSort}
                    canSelectAll={true}
                    onSelect={toggleSelectAll}
                    autoResize={true}
                >
                    {sortedIssuances.length === 0 ? (
                        <tr>
                            <td colSpan={12} className="py-8 text-center text-gray-500 dark:text-gray-400 text-lg">
                                Nenhuma emissão encontrada.
                            </td>
                        </tr>
                    ) : renderRows()}
                </ATable>
            </div>

            {/* Edit Modal */}
            <Dialog
                id="edit-issuance-modal"
                show={showEditModal.value}
                onClose={() => { showEditModal.value = false; editingIssuance.value = null; }}
                header="Editar Emissão"
                size="lg"
                body={editModalBody}
                footer={editModalFooter}
            />
        </div>
    );
};

export default IssuanceListPage;
